<template>
    <validation-observer ref="form" v-slot="{ meta, setValues }">
        <modal
            :id="modalId"
            title="Fetch Lodgix properties"
            scrollable
            ok-title="Save"
            :ok-disabled="!meta.valid || loading || saving"
            @ok.prevent="save"
            @hide="hideModal"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="save">
                <b-row v-show="!saving">
                    <b-col>
                        <p>Add the following properties to your {{ account }} account:</p>
                    </b-col>
                </b-row>
                <b-row v-show="!saving">
                    <b-col>
                        <validation-provider name="properties" v-slot="{ errors }" rules="required">
                            <search-multiselect-card
                                v-model="propertiesSelectedIDs"
                                :options="propertyOptions"
                                :error="errors[0]"
                                @input="val => setValues({ properties: val })"
                            ></search-multiselect-card>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-show="saving">
                    <b-col>
                        <div v-for="propertySelected in propertiesSelected">
                            <span>{{ propertySelected.name }}</span>
                            <span
                                v-if="propertySelected.status"
                                :class="[getStatusTextColorClass(propertySelected.status)]"
                            >
                                {{ getStatusDisplay(propertySelected.status) }}
                            </span>
                        </div>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import API from '../../../../api';
    import {
        DOOR_LOCKS_PROPERTIES_ENDPOINT
    } from '../../../../endpoints';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import SearchMultiselectCard from '../../../generic/search-multiselect-card/search-multiselect-card.vue';
    import Modal from '../../../generic/modal/modal.vue';

    const STATUS_PROCESSING = 1;
    const STATUS_ERROR = 2;
    const STATUS_DONE = 3;

    export default {
        name: 'DoorLockFetchLodgixPropertiesModal',
        components: {
            BusyModal,
            SearchMultiselectCard,
            Modal,
        },
        props: {
            modalId: String,
            account: String,
            process: {
                type: Function,
                default: () => Promise.resolve(),
            },
        },
        mounted() {
            (async () => {
                this.loading = true;
                try {
                    await this.fetchProperties();
                } finally {
                    this.loading = false;
                }
            })();
        },
        data() {
            return {
                loading: false,
                saving: false,
                properties: [],
                propertiesSelected: [],
                propertiesSelectedIDs: [],
            }
        },
        computed: {
            propertyOptions() {
                return this.properties.map(property => ({
                    text: property.name,
                    value: property.id,
                }))
            }
        },
        methods: {
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            getStatusDisplay(status) {
                switch(status) {
                    case STATUS_PROCESSING:
                        return 'processing...';
                    case STATUS_ERROR:
                        return 'error';
                    case STATUS_DONE:
                        return 'done';
                    default:
                        return '';
                }
            },
            getStatusTextColorClass(status) {
                switch(status) {
                    case STATUS_PROCESSING:
                        return 'text-secondary';
                    case STATUS_ERROR:
                        return 'text-danger';
                    case STATUS_DONE:
                        return 'text-success';
                    default:
                        return '';
                }
            },
            async fetchProperties() {
                try {
                    const response = await API.get(DOOR_LOCKS_PROPERTIES_ENDPOINT());
                    this.properties = response.data;
                } catch {
                    this.errorToast({text:'Could not load properties.'})
                }
            },
            async save() {
                this.saving = true;

                try {
                    let counter = 0;
                    for (const propertySelected of this.propertiesSelected) {
                        propertySelected.status = STATUS_PROCESSING;
                        try {
                            await this.process(propertySelected);
                            propertySelected.status = STATUS_DONE;
                            this.successToast({text: `Fetched "${propertySelected.name}".`})
                            counter += 1;
                        } catch (err) {
                            propertySelected.status = STATUS_ERROR;
                            this.errorToast({text:`Could not fetch "${propertySelected.name}".`})
                        }
                    }
                    this.successToast({text: `Fetched ${counter} properties.`})
                    this.$emit('success-save');
                    this.$nextTick(() => {
                        this.hideModal();
                    })
                } finally {
                    this.saving = false;
                }
            },
        },
        watch: {
            propertiesSelectedIDs(propertiesSelectedIDs) {
                this.propertiesSelected = propertiesSelectedIDs.map(id => {
                    const property = this.properties.find(property => property.id === id);
                    if (property) {
                        return {
                            ...property,
                            status: null,
                        };
                    }
                })
            }
        }
    }
</script>